import { apiFetch } from 'modules/shared/fetch';
import { createCorrelationId } from 'utils/createUniqueId';

import {
  OnboardingAnswers,
  OnboardingChart,
  OnboardingDocLink,
  OnboardingEnrollment,
  OnboardingQuestion,
  OnboardingRequestParams,
} from './payoutsOnboarding.types';

const ONBOARDING_QUESTIONS_URL = '/payouts/onboarding/questions';
const ONBOARDING_ENROLLMENTS_URL = '/payouts/onboarding/enrollments';
const ONBOARDING_CHARTS_URL = '/payouts/onboarding/charts';
const ONBOARDING_DOC_LINKS_URL = '/payouts/onboarding/doc-links';
const ONBOARDING_POSTMAN_COLLECTION_URL = '/payouts/onboarding/postman-collection';

// apiFetch is untyped JS; keep the options loosely typed like the other
// payout services do.
const requestOptions = (
  { appToken, appEnvironment }: OnboardingRequestParams,
  extra: Record<string, unknown> = {},
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any => ({
  appEnvironment,
  headers: {
    'lean-app-token': appToken,
    'lean-correlation-id': createCorrelationId(),
  },
  ...extra,
});

export default class PayoutsOnboardingService {
  static async getQuestions(
    params: OnboardingRequestParams,
  ): Promise<OnboardingQuestion[]> {
    const response = await apiFetch(
      ONBOARDING_QUESTIONS_URL,
      requestOptions(params),
    );

    return response?.data;
  }

  static async getEnrollment(
    params: OnboardingRequestParams,
  ): Promise<OnboardingEnrollment> {
    const response = await apiFetch(
      ONBOARDING_ENROLLMENTS_URL,
      requestOptions(params),
    );

    return response?.data;
  }

  static async createEnrollment({
    answers,
    ...params
  }: OnboardingRequestParams & {
    answers: OnboardingAnswers;
  }): Promise<OnboardingEnrollment> {
    const response = await apiFetch(
      ONBOARDING_ENROLLMENTS_URL,
      requestOptions(params, { method: 'POST', body: { answers } }),
    );

    return response?.data;
  }

  static async getCharts(
    params: OnboardingRequestParams,
  ): Promise<OnboardingChart[]> {
    const response = await apiFetch(
      ONBOARDING_CHARTS_URL,
      requestOptions(params),
    );

    return response?.data;
  }

  static async getDocLinks(
    params: OnboardingRequestParams,
  ): Promise<OnboardingDocLink[]> {
    const response = await apiFetch(
      ONBOARDING_DOC_LINKS_URL,
      requestOptions(params),
    );

    return response?.data;
  }

  static async getPostmanCollection(
    params: OnboardingRequestParams,
  ): Promise<unknown> {
    const response = await apiFetch(
      ONBOARDING_POSTMAN_COLLECTION_URL,
      requestOptions(params),
    );

    return response?.data;
  }
}
